import {
  Anchor,
  Divider,
  Drawer,
  Group,
  ScrollArea,
  Title,
  TypographyStylesProvider,
} from "@mantine/core";
import { atom, useAtom, useSetAtom } from "jotai";
import { ReactNode, Suspense } from "react";
import { IoSchool } from "react-icons/io5";
import { RiQuestionLine } from "react-icons/ri";
import { css } from "vite-plugin-inline-css-modules";

const LabelExplainerDrawerContent = atom<ReactNode | null>(null);
const LabelExplainerDrawerZIndex = atom<number | null>(null);

export const LabelExplainer = ({
  label,
  explanation,
  preventDefault,
  showIcon = false,
  inline = false,
  justify = "start",
  lh = 1,
  removeUnderline = false,
  zIndex,
}: {
  explanation: ReactNode | string;
  label: ReactNode | string;
  preventDefault?: boolean;
  showIcon?: boolean;
  inline?: boolean;
  justify?: "start" | "end" | "center" | "between" | "around" | "evenly";
  lh?: number | "inherit";
  removeUnderline?: boolean;
  zIndex?: number;
}) => {
  const setContent = useSetAtom(LabelExplainerDrawerContent);
  const setZIndex = useSetAtom(LabelExplainerDrawerZIndex);

  const onClick = (e: React.MouseEvent) => {
    if (preventDefault) e.preventDefault();
    setContent(explanation);
    setZIndex(zIndex || null);
  };

  return (
    <Anchor
      underline="always"
      inherit
      onClick={onClick}
      style={{
        textDecorationStyle: "dotted",
        verticalAlign: "top",
        textDecorationThickness: 1,
        textDecorationColor: removeUnderline
          ? "transparent"
          : "var(--mantine-color-accent-5)",

        lineHeight: lh !== "inherit" ? lh : undefined,
        cursor: "help",
      }}
    >
      {inline && (
        <span style={{ marginRight: 4 }}>
          {label}
          {showIcon && <RiQuestionLine />}
        </span>
      )}
      {!inline && (
        <Group gap={4} align="center" justify={justify} lh={lh}>
          {label} {showIcon && <RiQuestionLine />}
        </Group>
      )}
    </Anchor>
  );
};

export const LabelExplainerDrawer = () => {
  const [content, setContent] = useAtom(LabelExplainerDrawerContent);
  const [zIndex] = useAtom(LabelExplainerDrawerZIndex);
  const opened = content !== null;
  const close = () => setContent(null);

  return (
    <Drawer
      {...(zIndex !== null && { zIndex })}
      opened={opened}
      onClose={close}
      position="right"
      title={
        <Group justify="center" align="center" pl="sm">
          <IoSchool size={30} />
          <Title order={3} pt={4}>
            Learning Center
          </Title>
        </Group>
      }
      offset={8}
      padding={0}
      styles={{
        close: {
          marginRight: 6,
        },
      }}
    >
      <Divider mb={"md"} />
      <ScrollArea h={"calc(100vh - 130px)"} pl={"sm"} offsetScrollbars>
        <TypographyStylesProvider p={0} classNames={classes}>
          <Suspense fallback={"..."}>{content}</Suspense>
        </TypographyStylesProvider>
      </ScrollArea>
    </Drawer>
  );
};

const classes = css`
  .root {
    h1,
    h2,
    h3 {
      margin-top: 0px !important;
    }

    li {
      margin-bottom: 10px;
    }
  }
`;
